import React, { useEffect, useState } from 'react';
import {
  Box,
  Stack,
  Typography,
  useMediaQuery,
  Button,
  CircularProgress,
  Theme,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BoltIcon from '@mui/icons-material/Bolt';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { AdminCompany, OnboardingCompanyStatus } from 'src/modules/companies/types';
import { useCompaniesApi } from 'src/modules/companies/api';
import { User } from 'src/constants/types';
import { generateUrlWithCompany } from 'src/helpers/helpers';
import { AccentCard } from '../AccentCard';
import { CircledLabel } from './CircledLabel.styles';
import { ProgressBar } from '../ProgressBar';
import { ProcessingStateView } from '../ProcessingStateView/ProcessingStateView';



interface OnboardingWidgetProps {
  selectedCompanyId?: string;
  company?: AdminCompany | null;
  profile?: User | null;
  onCompanyRefetch?: () => void;
}

const OnboardingWidget: React.FC<OnboardingWidgetProps> = ({
  selectedCompanyId,
  company,
  profile,
  onCompanyRefetch,
}) => {
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { getOnboardingStatus, completeOnboarding } = useCompaniesApi();
  const [onboardingStatus, setOnboardingStatus] = useState<OnboardingCompanyStatus | null>(null);
  const [startOnboardingLoading, setStartOnboardingLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);

  useEffect(() => {
    if (company?.onboardingStatus === 'draft' && selectedCompanyId) {
      setStatusLoading(true);
      getOnboardingStatus(selectedCompanyId)
        .then(res => setOnboardingStatus(res.data))
        .catch((err) => {
          console.error('Failed to get onboarding status', err);
          setOnboardingStatus(null)
        })
        .finally(() => setStatusLoading(false));
    } else {
      setOnboardingStatus(null);
      setStatusLoading(false);
    }
  }, [company?.onboardingStatus, selectedCompanyId, getOnboardingStatus]);


  const isOnboardingProcessingOrFailed = company?.onboardingStatus === 'processing' || company?.onboardingStatus === 'failed';

  const MIN_ESTIMATED_MINUTES = 3;
  const MINUTES_PER_STEP = 2;

  const requiredSteps = onboardingStatus?.steps?.filter(s => s.required) || [];
  const completedSteps = requiredSteps.filter(s => s.completed && !['campaign_info', 'add_phone_number'].includes(s.step));
  const incompleteSteps = requiredSteps.filter(s => !s.completed);
  const completedCount = onboardingStatus?.completedCount ?? completedSteps.length;
  const totalSteps = requiredSteps.length;
  const progressPercent = totalSteps > 0 ? Math.round((completedCount / totalSteps) * 100) : 0;
  const stepsRemaining = onboardingStatus?.stepsRemaining ?? incompleteSteps.length;
  const estimatedMinutes = onboardingStatus?.estimatedMinutes ?? Math.max(MIN_ESTIMATED_MINUTES, incompleteSteps.length * MINUTES_PER_STEP);
  const showStartOnboarding = onboardingStatus?.canComplete === true && onboardingStatus?.onboardingStatus === 'draft';

  const handleFinish = () => {
    navigate(generateUrlWithCompany('/onboarding', selectedCompanyId || ''));
  };

  const handleStartOnboarding = async () => {
    if (!selectedCompanyId) return;
    setStartOnboardingLoading(true);
    try {
      await completeOnboarding(selectedCompanyId);
      toast.success('Account setup started');
      onCompanyRefetch?.();
    } catch {
      toast.error('Failed to start account setup');
    } finally {
      setStartOnboardingLoading(false);
    }
  };

  const WIDGET_HEIGHT = 190;

  if (isOnboardingProcessingOrFailed) {
    return <ProcessingStateView profile={profile} />;
  }

  const incompletedStepsCard = {
    title: 'Complete Now',
    count: incompleteSteps.length,
    description: `${incompleteSteps.length} task${incompleteSteps.length !== 1 ? 's' : ''} ~${estimatedMinutes} minutes`,
    icon: BoltIcon,
    accent: 'warning' as const,
    accentShade: 'light' as const,
  };


  const completedStepsCard = {
    title: 'Completed',
    count: completedCount,
    description: `${completedCount} step${completedCount !== 1 ? 's' : ''} finished`,
    icon: CheckCircleIcon,
    accent: 'success' as const,
    accentShade: 'main' as const,
  };

  const statusCards = incompleteSteps.length > 0 ? [incompletedStepsCard, completedStepsCard] : [completedStepsCard];

  if (company?.onboardingStatus === 'draft' && statusLoading) {
    return (
      <Stack
        width="100%"
        maxHeight={WIDGET_HEIGHT}
        minHeight={176}
        px={2}
        alignItems="center"
        justifyContent="center"
      >
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <Box width="100%" maxHeight={WIDGET_HEIGHT} px={2} py={1.5}>
      <Stack
        direction={isMobile ? 'column' : 'row'}
        alignItems={isMobile ? 'stretch' : 'center'}
        spacing={2}
        useFlexGap
      >
        <Stack direction="row" alignItems="center" spacing={2} flex={1} useFlexGap>
          <Stack width={56} height={56} alignItems="center" justifyContent="center">
            <CircledLabel variant="body1">{progressPercent}%</CircledLabel>
          </Stack>
          <Box flex={1} minWidth={0}>
            <Typography variant="subtitle1" fontWeight="fontWeightSemiBold">
              Setup Progress
            </Typography>
            <Box mt={1}>
              <ProgressBar thickness={8} variant="determinate" value={progressPercent} />
            </Box>
          </Box>
        </Stack>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} useFlexGap>
          <Typography variant="body2" color="text.secondary">
            {stepsRemaining} step{stepsRemaining !== 1 ? 's' : ''} remaining
          </Typography>
          {showStartOnboarding ? (
            <Button
              variant="contained"
              size="small"
              onClick={handleStartOnboarding}
              disabled={startOnboardingLoading}
              sx={{ flexShrink: 0, minWidth: 140 }}
            >
              {startOnboardingLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                'Start Account Setup'
              )}
            </Button>
          ) : (
            <Button variant="contained" size="small" onClick={handleFinish} sx={{ flexShrink: 0 }}>
              Finish ({estimatedMinutes} min)
            </Button>
          )}
        </Stack>
      </Stack>

      <Stack direction={isMobile ? 'column' : 'row'} spacing={1.5} mt={2.5}>
        {statusCards.map(card => {
          const Icon = card.icon;
          const interactive = card.title === 'Complete Now' && card.count > 0;
          return (
            <AccentCard
              key={card.title}
              variant="outlined"
              accent={card.accent}
              accentShade={card.accentShade}
              interactive={interactive}
              sx={{ flex: 1, p: 1.5 }}
              onClick={interactive ? handleFinish : undefined}
            >
              <Stack direction="row" alignItems="flex-start" spacing={1} useFlexGap>
                <Icon fontSize="small" sx={{ mt: 0.25 }} />
                <Box flex={1} minWidth={0}>
                  <Typography variant="body2" fontWeight="fontWeightSemiBold">
                    {card.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {card.description}
                  </Typography>
                </Box>
              </Stack>
            </AccentCard>
          );
        })}
      </Stack>
    </Box>
  );
};

export default OnboardingWidget;
